import { FC, memo, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { TableBody as Body } from '@mui/material';

import { Badge, CustomAlert, CustomButton, Text } from '../../../shared/uiComponents';
import { CustomTableRow, RowTableCell } from '../../../shared/uiComponents/Table/tableHelpers';
import { BadgeTypes, SoapNoteStatusTypes } from '../../../shared/uiComponents/StatusBadge';
import { BadgeCheckIcon } from '../../../shared/Assets/svgIcons';
import { findExpiredInfoColor } from '../helpers';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import {
	AuthorizationRowRendererProps,
	SessionsRowRendererProps,
	TemporaryAuthorizationRowRendererProps,
} from '../types';
import EditSessionDate from './EditSessionDate';

import { DispatchProperties } from '../../../redux/store';
import { getTemporaryAuthorizations } from '../../../redux/State/clientSlice/dashboardSlice';
import { updateAuthorization } from '../../../redux/State/clientSlice/insuranceAuthorizationSlice';

export const TableBodyAuthorization: FC<AuthorizationRowRendererProps> = memo(({ data }) => {
	const rows = useMemo(
		() =>
			data.map((row) => (
				<CustomTableRow key={`${row.client.id}-${row.authorization.id}`}>
					<RowTableCell>
						<Link to={`/clients/client/${row.client.id}`} target="_blank">
							<Text
								title={`${row.client.fullName}${row.authorization.isTemporary ? ' (Temporary)' : ''}`}
								style={{
									fontStyle: row.authorization.isTemporary ? 'italic' : 'normal',
								}}
								type={row.authorization.isTemporary ? 'psm' : 'pmd'}
							/>
						</Link>
					</RowTableCell>
					<RowTableCell>
						<Badge
							label={`${dayjs(row.authorization.endDate).format('MM-DD-YYYY')}`}
							sizeType="md"
							type={findExpiredInfoColor({
								endDate: row.authorization.endDate,
							})}
						/>
					</RowTableCell>
				</CustomTableRow>
			)),
		[data],
	);

	return <Body>{rows}</Body>;
});

TableBodyAuthorization.displayName = 'TableBodyAuthorization';

const TemporaryAuthorizationActions: FC<{ row: any }> = ({ row }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const [openActivate, setOpenActivate] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const activateHandler = () => {
		setOpenActivate(true);
	};

	const confirmActivateHandler = () => {
		const { temporaryAuthorization } = row;

		const data = {
			clientId: row.client.id,
			authorizationId: temporaryAuthorization.id,
			authorizationDetails: temporaryAuthorization.authorizationDetails,
			endDate: temporaryAuthorization.endDate,
			startDate: temporaryAuthorization.startDate,
			isTemporary: false,
		};

		setLoading(true);
		dispatch(updateAuthorization(data))
			.then(unwrapResult)
			.then(() => {
				toast('Authorization activated successfully');
				setOpenActivate(false);

				// Refresh temporary authorizations table
				const page = params.get('tempAuthPage') || '1';
				const pageSize = params.get('tempAuthPageSize') || '8';
				dispatch(getTemporaryAuthorizations({ page, pageSize }));
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	return (
		<>
			<div style={{ cursor: 'pointer' }} onClick={activateHandler}>
				<BadgeCheckIcon />
			</div>
			<CustomAlert
				open={openActivate}
				onClose={() => setOpenActivate(false)}
				title="Activate Authorization"
				Content={({ onClose }) => (
					<div style={{ textAlign: 'center' }}>
						<Text
							title="Are you sure you want to activate this authorization?"
							type="h4"
							font="header"
							fontWeight="semiBold"
							className="marginBottom16"
						/>
						<CustomButton
							title="Activate"
							onClick={confirmActivateHandler}
							loading={loading}
							className="marginBottom16"
						/>
						<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} />
					</div>
				)}
			/>
		</>
	);
};

export const TableBodyTemporaryAuthorization: FC<TemporaryAuthorizationRowRendererProps> = memo(({ data }) => {
	const rows = useMemo(
		() =>
			data.map((row) => (
				<CustomTableRow key={`${row.client.id}-${row.temporaryAuthorization.id}`}>
					<RowTableCell>
						<Link to={`/clients/client/${row.client.id}`} target="_blank">
							<Text title={row.client.fullName as string} />
						</Link>
					</RowTableCell>
					<RowTableCell>
						<Badge
							label={`${dayjs(row.temporaryAuthorization.endDate).format('MM-DD-YYYY')}`}
							sizeType="md"
							type={findExpiredInfoColor({
								endDate: row.temporaryAuthorization.endDate,
							})}
						/>
					</RowTableCell>
					<RowTableCell>
						<TemporaryAuthorizationActions row={row} />
					</RowTableCell>
				</CustomTableRow>
			)),
		[data],
	);

	return <Body>{rows}</Body>;
});

export const TableBodySessions: FC<SessionsRowRendererProps> = memo(({ data }) => {
	const rows = useMemo(
		() =>
			data.map((row, index) => (
				<CustomTableRow key={`${row.client.id}-${index}`}>
					<RowTableCell>{row.user.fullName}</RowTableCell>
					<RowTableCell>{row.client.fullName}</RowTableCell>
					<RowTableCell>{row.sessionType.name}</RowTableCell>
					<RowTableCell>{dayjs(row.date).format('MM/DD/YYYY')}</RowTableCell>
					{row.reportStatus && (
						<RowTableCell>
							<Badge
								label={row.reportStatus?.name ?? 'Not created'}
								sizeType="sm"
								type={SoapNoteStatusTypes[row?.reportStatus?.status ?? 'gray'] as BadgeTypes}
							/>
						</RowTableCell>
					)}
					<RowTableCell>
						<EditSessionDate row={row} />
					</RowTableCell>
				</CustomTableRow>
			)),
		[data],
	);

	return <Body>{rows}</Body>;
});
TableBodyTemporaryAuthorization.displayName = 'TableBodyTemporaryAuthorization';
TableBodySessions.displayName = 'TableBodySessions';
